import { useState, useEffect, useRef } from "react";
import {
  ProjectDetails,
  projectFieldErrors,
  projectTouch,
  ProjectStatus,
} from "../../utils/interface/project";
import { validateProjectForm, extractApiError, PROJECT_FIELD_LIMITS } from "../../utils/util";
import { createProject, updateProject } from "../../api/Projects";
import { uploadThumbnail, validateExcelFile } from "../../api/upload/file";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";
import FormField from "./FormField";
import TextAreaField from "./TextAreaField";
import SelectField from "./SelectField";
import UserSearchDropdown from "./UserSearchDropdown";
import ThumbnailUpload from "./ThumbnailUpload";
import ApiErrorDisplay from "./ApiErrorDisplay";
import SubmitButton from "./SubmitButton";

interface CreateProjectProps {
  isEditMode?: boolean;
  initialData?: ProjectDetails & { id?: string; ifcModel?: string | string[] | { url?: string; name?: string } | null; projectThumbnailPreview?: string | null };
  refreshProjects?: () => void;
  setIsModalOpen?: (open: boolean) => void;
  onSuccess?: () => void;
  isViewMode: boolean;
  layout?: "modal" | "page";
}

/** Get display filename from upload URL (e.g. control panel doc). */
function getFileNameFromUrl(url: string | null | undefined): string {
  if (!url || typeof url !== "string") return "";
  try {
    const segment = url.split("?")[0].split("/").pop() ?? "";
    return decodeURIComponent(segment);
  } catch {
    return url;
  }
}

const CreateProject = ({
  isEditMode = false,
  isViewMode = false,
  initialData,
  refreshProjects,
  setIsModalOpen,
  onSuccess,
  layout = "modal",
}: CreateProjectProps) => {
  const { userDetails } = useAuth();

  const [projectDetails, setProjectDetails] = useState<ProjectDetails>({
    name: initialData?.name || "",
    summary: initialData?.summary || "",
    companyName: initialData?.companyName || "",
    projectThumbnail: initialData?.projectThumbnail || "",
    status: initialData?.status || ProjectStatus.DRAFT,
    files: initialData?.files || [],
    userId: initialData?.userId ?? "",
    location: initialData?.location || "",
  });

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>("");
  const [originalThumbnailUrl, setOriginalThumbnailUrl] = useState<string>("");
  const [controlPanelFile, setControlPanelFile] = useState<File | null>(null);
  const controlPanelInputRef = useRef<HTMLInputElement>(null);
  const existingControlPanelFileName = getFileNameFromUrl(initialData?.uploadControlPanelDoc);
  const controlPanelDisplayName = controlPanelFile?.name ?? existingControlPanelFileName;
  const [touched, setTouched] = useState<projectTouch>({});
  const [errors, setErrors] = useState<projectFieldErrors>({});
  const [apiError, setApiError] = useState<string>("");
  const [loading, setLoading] = useState(false);

  // Update form when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData && isEditMode) {
      setProjectDetails({
        name: initialData.name || "",
        summary: initialData.summary || "",
        companyName: initialData.companyName || "",
        projectThumbnail: initialData.projectThumbnail || "",
        status: initialData.status || ProjectStatus.DRAFT,
        files: initialData.files || [],
        userId: initialData.userId ?? "",
        location: initialData.location || "",
      });
      // Set thumbnail preview for display (prefer preview URL from API)
      const thumbnailDisplayUrl = initialData.projectThumbnailPreview ?? initialData.projectThumbnail;
      if (thumbnailDisplayUrl) {
        setThumbnailPreview(thumbnailDisplayUrl);
        setOriginalThumbnailUrl(thumbnailDisplayUrl);
      }
    }
  }, [initialData, isEditMode, userDetails]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    setProjectDetails((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    setApiError(""); // Clear API error when user types

    const updated = { ...projectDetails, [name]: value };
    setErrors(validateProjectForm(updated));
  };

  const handleThumbnailChange = (file: File, preview: string) => {
    setThumbnailFile(file);
    setThumbnailPreview(preview);
    setTouched((prev) => ({ ...prev, projectThumbnail: true }));
    setApiError("");
    setProjectDetails((prev) => ({ ...prev, projectThumbnail: file.name }));
  };

  const handleThumbnailRemove = () => {
    setThumbnailFile(null);
    // If a new file was selected and removed, restore original in edit mode
    // Otherwise, clear the thumbnail
    if (isEditMode && originalThumbnailUrl) {
      setThumbnailPreview(originalThumbnailUrl);
      setProjectDetails((prev) => ({
        ...prev,
        projectThumbnail: originalThumbnailUrl,
      }));
    } else {
      setThumbnailPreview("");
      setProjectDetails((prev) => ({ ...prev, projectThumbnail: "" }));
    }
    setTouched((prev) => ({ ...prev, projectThumbnail: false }));
  };

  const handleBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validateProjectForm(projectDetails));
  };

  const handleUserChange = (userId: string) => {
    setProjectDetails((prev) => ({ ...prev, userId }));
    setTouched((prev) => ({ ...prev, userId: true }));
    setApiError("");

    // Validate after selection
    const updated = { ...projectDetails, userId };
    setErrors(validateProjectForm(updated));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");

    // Use projectDetails as-is; only include userId in payload when a user is assigned
    const finalProjectDetails = {
      ...projectDetails,
      userId: projectDetails.userId ?? "",
    };

    // Mark all fields as touched
    setTouched({
      name: true,
      status: true,
      uploadControlPanelDoc: true,
    });

    const validationErrors = validateProjectForm(finalProjectDetails);

    // Override thumbnail validation if we have a file
    if (thumbnailFile && validationErrors.projectThumbnail) {
      delete validationErrors.projectThumbnail;
    }

    // Control Panel Excel document is required (create and edit)
    const hasControlPanelDoc =
      controlPanelFile ||
      (isEditMode && initialData?.uploadControlPanelDoc);
    if (!hasControlPanelDoc) {
      validationErrors.uploadControlPanelDoc =
        "Control Panel Excel document is required.";
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      setLoading(true);

      let thumbnailValue = finalProjectDetails.projectThumbnail;

      // Upload thumbnail first if a new file is selected (in both create and edit modes)
      if (thumbnailFile) {
        try {
          const downloadUrl = await uploadThumbnail(
            thumbnailFile,
            "projectThumbnails",
          );
          thumbnailValue = downloadUrl;
          // Update the project details with the new URL
          setProjectDetails((prev) => ({
            ...prev,
            projectThumbnail: downloadUrl,
          }));
        } catch (uploadError) {
          const errorMessage = extractApiError(uploadError);
          setApiError(`Failed to upload thumbnail: ${errorMessage}`);
          setLoading(false);
          return;
        }
      }

      // Upload control panel Excel if a new file was selected; otherwise use existing in edit mode
      let uploadControlPanelDoc: string | undefined;
      if (controlPanelFile) {
        const cpanelError = await validateExcelFile(controlPanelFile);
        if (cpanelError) {
          setErrors((prev) => ({ ...prev, uploadControlPanelDoc: cpanelError }));
          setLoading(false);
          return;
        }
        try {
          uploadControlPanelDoc = await uploadThumbnail(
            controlPanelFile,
            "controlPanel",
          );
        } catch (uploadError) {
          const errorMessage = extractApiError(uploadError);
          setApiError(`Failed to upload control panel document: ${errorMessage}`);
          setLoading(false);
          return;
        }
      } else if (isEditMode && initialData?.uploadControlPanelDoc) {
        const existing =
          typeof initialData.uploadControlPanelDoc === "string"
            ? initialData.uploadControlPanelDoc
            : (initialData.uploadControlPanelDoc as { url?: string })?.url;
        if (existing) uploadControlPanelDoc = existing;
      }

      // Prepare payload; only include userId when a user is assigned (no default to current user)
      const payload = {
        name: finalProjectDetails.name.trim(),
        summary: finalProjectDetails.summary.trim(),
        companyName: finalProjectDetails.companyName.trim(),
        projectThumbnail: thumbnailValue,
        status: finalProjectDetails.status,
        ...(finalProjectDetails.userId?.trim() && { userId: finalProjectDetails.userId.trim() }),
        location: finalProjectDetails.location,
        ...(uploadControlPanelDoc != null && { uploadControlPanelDoc }),
      };

      if (isEditMode && initialData?.id) {
        await updateProject(initialData.id, payload);
        toast.success("Project updated successfully.");
      } else {
        await createProject(payload);
        toast.success("Project created successfully.");
      }

      // Reset form only if not in edit mode
      if (!isEditMode) {
        setProjectDetails({
          name: "",
          summary: "",
          companyName: "",
          projectThumbnail: "",
          status: ProjectStatus.DRAFT,
          files: [],
          userId: "",
          location: "",
        });
        setThumbnailFile(null);
        setThumbnailPreview("");
        setOriginalThumbnailUrl("");
        setControlPanelFile(null);
        if (controlPanelInputRef.current) controlPanelInputRef.current.value = "";
        setTouched({});
        setErrors({});
      }

      // Refresh projects list if callback provided
      if (refreshProjects) {
        refreshProjects();
      }

      if (onSuccess) {
        onSuccess();
      } else if (setIsModalOpen) {
        setIsModalOpen(false);
      }
    } catch (error) {
      const errorMessage = extractApiError(error);
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: ProjectStatus.DRAFT, label: "Draft" },
    { value: ProjectStatus.FILE_UPLOADED, label: "File Uploaded" },
    { value: ProjectStatus.ADMIN_REVIEW, label: "Admin Review" },
    { value: ProjectStatus.ADMIN_REJECTED, label: "Admin Rejected" },
    {
      value: ProjectStatus.ACCEPTED_FOR_PROCESSING,
      label: "Accepted for Processing",
    },
    { value: ProjectStatus.SCRIPT_RUNNING, label: "Script Running" },
    { value: ProjectStatus.INVALID_FILE, label: "Invalid File" },
    { value: ProjectStatus.RESULT_GENERATED, label: "Result Generated" },
    { value: ProjectStatus.ADMIN_RESULT_REVIEW, label: "Admin Result Review" },
    { value: ProjectStatus.REPORT_UPLOADED, label: "Report Uploaded" },
    { value: ProjectStatus.DONE, label: "Done" },
  ];

  const formContent = (
    <>
      <ApiErrorDisplay error={apiError} />

      {layout === "page" ? (
        <>
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-[25px] lg:gap-x-24"
            data-layout="page"
          >
            <div className="space-y-4">
              <FormField
                label="Project Name"
                name="name"
                value={projectDetails.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter project name"
                required
                touched={touched.name}
                error={errors.name}
                minLength={PROJECT_FIELD_LIMITS.name.min}
                maxLength={PROJECT_FIELD_LIMITS.name.max}
              />
              <FormField
                label="Location"
                name="location"
                value={projectDetails.location}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter project location"
                touched={touched.location}
                error={errors.location}
                minLength={PROJECT_FIELD_LIMITS.location.min}
                maxLength={PROJECT_FIELD_LIMITS.location.max}
              />
              <FormField
                label="Company"
                name="companyName"
                value={projectDetails.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter company name"
                touched={touched.companyName}
                error={errors.companyName}
                minLength={PROJECT_FIELD_LIMITS.companyName.min}
                maxLength={PROJECT_FIELD_LIMITS.companyName.max}
              />
            </div>
            <div className="space-y-6">
              <ThumbnailUpload
                value={projectDetails.projectThumbnail}
                preview={thumbnailPreview}
                file={thumbnailFile}
                onChange={handleThumbnailChange}
                onRemove={handleThumbnailRemove}
                onBlur={() =>
                  setTouched((prev) => ({ ...prev, projectThumbnail: true }))
                }
                touched={touched.projectThumbnail}
                error={errors.projectThumbnail}
              />
              {/* <SelectField
                                label="Status"
                                name="status"
                                value={projectDetails.status}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                options={statusOptions}
                                required
                                touched={touched.status}
                                error={errors.status}
                            /> */}
            </div>
            <div className="lg:col-span-2  ">
              <TextAreaField
                label="Summary"
                name="summary"
                value={projectDetails.summary}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter project summary"
                touched={touched.summary}
                error={errors.summary}
                textareaClassName="min-h-[150px]"
                minLength={PROJECT_FIELD_LIMITS.summary.min}
                maxLength={PROJECT_FIELD_LIMITS.summary.max}
              />
            </div>
            <div className="">
              <UserSearchDropdown
                value={projectDetails.userId}
                onChange={handleUserChange}
                onBlur={() => setTouched((prev) => ({ ...prev, userId: true }))}
                initialUserId={initialData?.userId}
                touched={touched.userId}
                error={errors.userId}
                variant="page"
                label="Assign user"
              />
            </div>
            <div className="">
              <label className="block text-[21px] font-weight-600  mb-[16px] text-[#E761A0]">
                Upload Control Panel Excel document <span className="text-red-500">*</span>
              </label>
              {/* {isEditMode && controlPanelDisplayName && (
                <p className="text-sm text-gray-600 mb-2 truncate max-w-[549px]" title={controlPanelDisplayName}>
                  {controlPanelDisplayName}
                </p>
              )} */}
              <div className="flex w-full max-w-[549px] h-[53px] rounded-full border border-[#868686] overflow-hidden  ">
                <input
                  ref={controlPanelInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      validateExcelFile(file).then((error) => {
                        if (error) {
                          setErrors((prev) => ({ ...prev, uploadControlPanelDoc: error }));
                          setControlPanelFile(null);
                          if (controlPanelInputRef.current) controlPanelInputRef.current.value = "";
                        } else {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.uploadControlPanelDoc;
                            return next;
                          });
                          setControlPanelFile(file);
                        }
                        setTouched((prev) => ({ ...prev, uploadControlPanelDoc: true }));
                      });
                    } else {
                      setControlPanelFile(null);
                      setTouched((prev) => ({ ...prev, uploadControlPanelDoc: true }));
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => controlPanelInputRef.current?.click()}
                  className="browse-file-btn shrink-0 px-9 h-full bg-white text-gray-800 text-base rounded-full font-semibold uppercase tracking-wide hover:bg-gray-100 transition-colors"
                >
                  BROWSE FILE
                </button>
                <input
                  type="text"
                  readOnly
                  value={controlPanelDisplayName}
                  placeholder="No file chosen"
                  className="flex-1 min-w-0 h-full px-4 bg-transparent text-gray-400 text-sm placeholder-gray-500 focus:outline-none border-0"
                />
              </div>
              {touched.uploadControlPanelDoc && errors.uploadControlPanelDoc && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.uploadControlPanelDoc}
                </p>
              )}
            </div>
          </div>
          <div className="mt-8 space-y-5" data-layout="page">
            <SubmitButton
              loading={loading}
              isEditMode={isEditMode}
              isViewMode={isViewMode}
              variant={layout === "page" ? "page" : undefined}
              className="theme-btn min-w-[178px] xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px] mt-6"
            />
          </div>
        </>
      ) : (
        <>
          <FormField
            label="Project Name"
            name="name"
            value={projectDetails.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter project name"
            required
            touched={touched.name}
            error={errors.name}
            minLength={PROJECT_FIELD_LIMITS.name.min}
            maxLength={PROJECT_FIELD_LIMITS.name.max}
          />
          <FormField
            label="Company Name"
            name="companyName"
            value={projectDetails.companyName}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter company name"
            touched={touched.companyName}
            error={errors.companyName}
            minLength={PROJECT_FIELD_LIMITS.companyName.min}
            maxLength={PROJECT_FIELD_LIMITS.companyName.max}
          />
          <FormField
            label="Location"
            name="location"
            value={projectDetails.location}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter project location"
            touched={touched.location}
            error={errors.location}
            minLength={PROJECT_FIELD_LIMITS.location.min}
            maxLength={PROJECT_FIELD_LIMITS.location.max}
          />
          <TextAreaField
            label="Summary"
            name="summary"
            value={projectDetails.summary}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter project summary"
            touched={touched.summary}
            error={errors.summary}
            minLength={PROJECT_FIELD_LIMITS.summary.min}
            maxLength={PROJECT_FIELD_LIMITS.summary.max}
          />
          <SelectField
            label="Status"
            name="status"
            value={projectDetails.status}
            onChange={handleChange}
            onBlur={handleBlur}
            options={statusOptions}
            required
            touched={touched.status}
            error={errors.status}
          />
          <UserSearchDropdown
            value={projectDetails.userId}
            onChange={handleUserChange}
            onBlur={() => setTouched((prev) => ({ ...prev, userId: true }))}
            initialUserId={initialData?.userId}
            touched={touched.userId}
            error={errors.userId}
          />
          <ThumbnailUpload
            value={projectDetails.projectThumbnail}
            preview={thumbnailPreview}
            file={thumbnailFile}
            onChange={handleThumbnailChange}
            onRemove={handleThumbnailRemove}
            onBlur={() =>
              setTouched((prev) => ({ ...prev, projectThumbnail: true }))
            }
            touched={touched.projectThumbnail}
            error={errors.projectThumbnail}
          />
          <SubmitButton
            loading={loading}
            isEditMode={isEditMode}
            isViewMode={isViewMode}
          />
        </>
      )}
    </>
  );

  return (
    <form
      onSubmit={handleSubmit}
      className={layout === "page" ? "space-y-0" : "space-y-5"}
    >
      {formContent}
    </form>
  );
};

export default CreateProject;
